/**
 * format-toggle.js
 * Poster/Canvas merge for NYC Restaurant Prints product pages.
 *
 * Flow:
 * 1. Fetch this product's own data via the public
 *    /products/{handle}.js endpoint (kept consistent with how the
 *    sibling gets fetched, rather than trusting Liquid's JSON shape
 *    to line up with it).
 * 2. Ask the Storefront API for a sibling product: same Title, the
 *    other Type. If none exists, leave the toggle hidden — the page
 *    just behaves like a normal single-product form.
 * 3. If a sibling exists, fetch its data the same way and cache it.
 * 4. Render option pickers generically from product.options /
 *    product.variants. Any option with only one possible value
 *    across all variants (e.g. a frame depth that's always 1.25")
 *    is skipped rather than shown as a dropdown.
 * 5. Switching the Poster/Canvas <select> swaps the active product
 *    in place (image, description, price, options, variant id) with
 *    no page reload, and updates the URL via history.replaceState.
 * 6. Add to cart posts to /cart/add.js via fetch, using whichever
 *    variant id is currently active.
 *
 * Dispatches a `format-toggle:change` CustomEvent on `document`
 * whenever the active product/variant changes, with
 * `detail: { product, variant }` — hook into this if you want your
 * theme's own image gallery or cart drawer to stay in sync.
 */
(function () {
  "use strict";

  // Bump this if Shopify deprecates the version below —
  // see https://shopify.dev/docs/api/usage/versioning
  var STOREFRONT_API_VERSION = "2025-10";

  // Friendlier customer-facing labels. Keys must match product.type
  // values exactly — these only change what's displayed, not the
  // underlying value used for matching/lookup.
  var FORMAT_LABELS = {
    Poster: "Poster (Unframed)",
    Canvas: "Canvas (Framed)"
  };

  // Friendlier labels for product options, keyed by the option's
  // actual Shopify name. Only changes the picker's <label> text —
  // the option name used for variant matching is untouched.
  var OPTION_LABELS = {
    Color: "Frame Color"
  };

  document.querySelectorAll("[data-format-toggle]").forEach(initFormatToggle);

  function initFormatToggle(root) {
    var shopDomain = root.dataset.shopDomain;
    var storefrontToken = root.dataset.storefrontToken;
    var handle = root.dataset.productHandle;
    var productTitle = root.dataset.productTitle;
    var productType = root.dataset.productType;
    var otherType = productType === "Poster" ? "Canvas" : "Poster";

    var switcherWrap = root.querySelector("[data-format-toggle-switcher]");
    var switcherSelect = root.querySelector("[data-format-toggle-select]");
    var optionsWrap = root.querySelector("[data-format-toggle-options]");
    var imageEl = root.querySelector("[data-format-toggle-image]");
    var prevBtn = root.querySelector("[data-format-toggle-prev]");
    var nextBtn = root.querySelector("[data-format-toggle-next]");
    var counterEl = root.querySelector("[data-format-toggle-counter]");
    var priceEl = root.querySelector("[data-format-toggle-price]");
    var descriptionEl = root.querySelector("[data-format-toggle-description]");
    var form = root.querySelector("[data-format-toggle-form]");
    var fallbackSelect = root.querySelector("[data-fallback-variant-select]");
    var submitBtn = root.querySelector("[data-format-toggle-submit]");
    var statusEl = root.querySelector("[data-format-toggle-status]");

    var products = {};
    var activeType = productType;
    var selectedOptions = {};
    var hiddenVariantInput = null;
    var currentImages = [];
    var currentIndex = 0;

    prevBtn.addEventListener("click", function () {
      showImage(currentIndex - 1);
    });
    nextBtn.addEventListener("click", function () {
      showImage(currentIndex + 1);
    });

    fetchProductJson(handle)
      .then(function (json) {
        products[productType] = json;
        enhance();
        renderProduct(products[activeType]);
      })
      .catch(function (err) {
        console.warn("[format-toggle] couldn't load product data, leaving no-JS fallback in place:", err);
      });

    findSibling(shopDomain, storefrontToken, productTitle, otherType)
      .then(function (siblingHandle) {
        if (!siblingHandle) return;
        return fetchProductJson(siblingHandle).then(function (json) {
          products[otherType] = json;
          enableSwitcher();
        });
      })
      .catch(function (err) {
        console.warn("[format-toggle] sibling lookup failed:", err);
      });

    function enhance() {
      // Replace the no-JS "id" select with a hidden input we control,
      // so the two can't both submit a value for "id".
      fallbackSelect.disabled = true;
      fallbackSelect.hidden = true;

      hiddenVariantInput = document.createElement("input");
      hiddenVariantInput.type = "hidden";
      hiddenVariantInput.name = "id";
      form.insertBefore(hiddenVariantInput, form.firstChild);

      form.addEventListener("submit", onSubmit);
    }

    function enableSwitcher() {
      switcherWrap.hidden = false;
      switcherSelect.innerHTML = "";
      [productType, otherType].forEach(function (type) {
        var opt = document.createElement("option");
        opt.value = type;
        opt.textContent = FORMAT_LABELS[type] || type;
        if (type === activeType) opt.selected = true;
        switcherSelect.appendChild(opt);
      });
      switcherSelect.addEventListener("change", function () {
        activeType = switcherSelect.value;
        selectedOptions = {};
        renderProduct(products[activeType]);
      });
    }

    function renderProduct(product) {
      if (!product) return;

      renderCarousel(product);
      imageEl.alt = product.title || "";
      descriptionEl.innerHTML = product.description || "";

      var requestedVariantId = new URLSearchParams(window.location.search).get("variant");
      var requestedVariant = product.variants.filter(function (v) {
        return String(v.id) === requestedVariantId;
      })[0];
      var defaultVariant =
        requestedVariant ||
        product.variants.filter(function (v) { return v.available; })[0] ||
        product.variants[0];

      selectedOptions = {};
      product.options.forEach(function (option, i) {
        selectedOptions[option.name] = defaultVariant["option" + (i + 1)];
      });

      renderOptionPickers(product);
      updateActiveVariant(product);
    }

    function renderCarousel(product) {
      var images = [];
      if (product.images && product.images.length) {
        images = product.images.map(imageSrc).filter(Boolean);
      }
      if (!images.length) {
        var fallback = imageSrc(product.featured_image);
        if (fallback) images = [fallback];
      }

      currentImages = images;
      currentIndex = 0;
      showImage(0);

      var multiple = currentImages.length > 1;
      prevBtn.hidden = !multiple;
      nextBtn.hidden = !multiple;
      counterEl.hidden = !multiple;
    }

    function showImage(index) {
      if (!currentImages.length) return;
      currentIndex = ((index % currentImages.length) + currentImages.length) % currentImages.length;
      imageEl.src = resizeImage(currentImages[currentIndex], 800);
      counterEl.textContent = (currentIndex + 1) + "/" + currentImages.length;
    }

    function jumpToImage(url) {
      if (!url) return;
      var target = resizeImage(url, 800);
      var idx = -1;
      for (var i = 0; i < currentImages.length; i++) {
        if (resizeImage(currentImages[i], 800) === target) {
          idx = i;
          break;
        }
      }
      if (idx === -1) {
        // Not one of this product's known images (e.g. a variant image we
        // haven't seen) — show it directly without disturbing carousel state.
        imageEl.src = target;
        return;
      }
      showImage(idx);
    }

    function renderOptionPickers(product) {
      optionsWrap.innerHTML = "";

      product.options.forEach(function (option, index) {
        var name = option.name;
        var key = "option" + (index + 1);
        var values = uniqueInOrder(product.variants.map(function (v) { return v[key]; }));

        // Skip options that never vary (e.g. a fixed frame depth).
        if (values.length <= 1) return;

        var wrap = document.createElement("div");
        wrap.className = "format-toggle__option";

        var selectId = "FormatOption-" + index + "-" + Math.random().toString(36).slice(2, 8);

        var label = document.createElement("label");
        label.textContent = OPTION_LABELS[name] || name;
        label.htmlFor = selectId;

        var select = document.createElement("select");
        select.id = selectId;
        select.dataset.optionName = name;

        values.forEach(function (value) {
          var opt = document.createElement("option");
          opt.value = value;
          opt.textContent = value;
          if (selectedOptions[name] === value) opt.selected = true;
          select.appendChild(opt);
        });

        select.addEventListener("change", function () {
          selectedOptions[name] = select.value;
          updateActiveVariant(product);
        });

        wrap.appendChild(label);
        wrap.appendChild(select);
        optionsWrap.appendChild(wrap);
      });
    }

    function updateActiveVariant(product) {
      var variant = product.variants.filter(function (v) {
        return product.options.every(function (option, i) {
          return v["option" + (i + 1)] === selectedOptions[option.name];
        });
      })[0];

      if (!variant) {
        submitBtn.disabled = true;
        return;
      }

      if (hiddenVariantInput) hiddenVariantInput.value = variant.id;
      priceEl.textContent = formatMoney(variant.price);
      submitBtn.disabled = !variant.available;
      submitBtn.textContent = variant.available ? "Add to cart" : "Sold out";

      var variantImg = imageSrc(variant.featured_image);
      if (variantImg) jumpToImage(variantImg);

      var nextUrl = new URL(window.location.href);
      nextUrl.pathname = "/products/" + product.handle;
      nextUrl.searchParams.set("variant", variant.id);
      history.replaceState(null, "", nextUrl.pathname + nextUrl.search);

      document.dispatchEvent(
        new CustomEvent("format-toggle:change", { detail: { product: product, variant: variant } })
      );
    }

    function onSubmit(event) {
      event.preventDefault();
      if (!hiddenVariantInput || !hiddenVariantInput.value) return;

      submitBtn.disabled = true;
      statusEl.hidden = true;

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: Number(hiddenVariantInput.value), quantity: 1 }],
        }),
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (e) { return Promise.reject(e); });
          return res.json();
        })
        .then(function () {
          statusEl.hidden = false;
          statusEl.textContent = "Added to cart.";
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        })
        .catch(function (err) {
          statusEl.hidden = false;
          statusEl.textContent = (err && err.description) || "Couldn't add that to your cart.";
        })
        .finally(function () {
          submitBtn.disabled = false;
        });
    }
  }

  function findSibling(shopDomain, token, title, type) {
    if (!token || token.indexOf("PASTE_YOUR") === 0) {
      console.warn("[format-toggle] No Storefront API token configured — skipping sibling lookup.");
      return Promise.resolve(null);
    }

    var escapedTitle = title.replace(/'/g, "\\'");
    var searchQuery = "title:'" + escapedTitle + "' AND product_type:'" + type + "'";

    var query =
      "query FindSibling($query: String!) {" +
      "  products(first: 5, query: $query) {" +
      "    edges { node { handle title productType } }" +
      "  }" +
      "}";

    return fetch("https://" + shopDomain + "/api/" + STOREFRONT_API_VERSION + "/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({ query: query, variables: { query: searchQuery } }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var edges = data && data.data && data.data.products && data.data.products.edges;
        if (!edges || !edges.length) return null;
        var match = edges.filter(function (e) {
          return e.node.title === title && e.node.productType === type;
        })[0];
        return match ? match.node.handle : null;
      });
  }

  function fetchProductJson(handle) {
    return fetch("/products/" + handle + ".js").then(function (res) {
      if (!res.ok) return Promise.reject(new Error("Product fetch failed: " + handle));
      return res.json();
    });
  }

  function imageSrc(img) {
    if (!img) return null;
    return typeof img === "string" ? img : img.src;
  }

  function uniqueInOrder(arr) {
    var seen = {};
    var out = [];
    arr.forEach(function (v) {
      if (v != null && !seen[v]) {
        seen[v] = true;
        out.push(v);
      }
    });
    return out;
  }

  function resizeImage(url, width) {
    if (!url) return url;
    return url.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, "_" + width + "x$1$2");
  }

  function formatMoney(cents) {
    return "$" + (cents / 100).toFixed(2);
  }
})();
